import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchGetChatUsers } from 'api/chat';
import { fetcher } from 'utils/axios';
import { ChatViewId, ChatViewUsersId, getCurrentView } from 'hooks/useCurrentChatView';
import { dispatch, store } from 'store';
import { ChatUserModel } from 'types/chat-users/chat-user';
import { ChatUserLabel } from 'types/chat-users/chat-user-label';
import { ChatUsersFilters, ChatUsersFiltersRequest } from 'types/chat-users/filters';
import { HistoryMessage, MessageModel } from 'types/chat/messages/message';
import { ApplicationUser } from 'types/company/applicationUser';
import { getSessionIdFromStore, readSessionPutData, resolveSessionIdForMutation, type UpdateSessionResponse } from 'api/sessions';
import { doesUserMatchAdditionalFilters, doesUserMatchCurrentFilters } from 'utils/chat';
import { migrateCustomView, isFilterGroup } from 'utils/migrate-custom-views';
import { buildSendFailedFilterGroups } from 'utils/awaiting-customer-send-failed';
import { getPathname } from 'utils/helpers';
import { normalizePhoneForChatUserApi } from 'api/contacts-messaging';
import {
  mergeSnoozeTimesIntoChatUsers,
  shouldSortChatUsersByReminderDate,
  sortChatUsersByReminderDateAsc,
} from 'utils/chat-workflow-overrides';
import type { WorkflowStatusId } from 'types/workflow/workflow-status';
import { resolveStoreCompanyId } from 'utils/resolve-chat-user-company-id';
import { scheduleUnreadCountsRefresh } from 'utils/unread-counts-refresh';

function stampMissingCompanyIdOnChatUsers(users: ChatUserModel[]): ChatUserModel[] {
  const companyId = resolveStoreCompanyId();
  if (!companyId) return users;
  return users.map((user) => (user.CompanyID ? user : { ...user, CompanyID: companyId }));
}

/** גודל עמוד ברשימת השיחות — קבוע; השרת חותך כל בקשה מעל 100 (MAX_PAGE_SIZE בנוד) */
export const CHAT_LIST_PAGE_SIZE = 30;

export type ChatUsersPagingState = {
  /** כמה שורות שרת נצרכו עד כה (ה-offset של העמוד הבא). לא אורך הרשימה המוצגת —
   *  סינון בצד הלקוח (הסתרת "ממתין ללקוח" וכו') מצמצם את המוצג בלי לשנות את זה. */
  loadedOffset: number;
  /** סך השורות לפי pagination.total מהשרת; null כשלא התקבל */
  total: number | null;
  /** גודל העמוד האחרון שחזר — עצירה כשאין total וחזר עמוד חסר */
  lastPageSize: number;
};

export function hasMoreChatUsers(paging: ChatUsersPagingState | undefined): boolean {
  if (!paging) return false;
  if (paging.total != null) return paging.loadedOffset < paging.total;
  return paging.lastPageSize >= CHAT_LIST_PAGE_SIZE;
}

/** צירוף עמוד חדש לרשימה קיימת בלי כפילויות — הזהות ברשימה היא מספר הטלפון (כמו
 *  בכל מנגנוני הסוקט); שורה שכבר קיימת נשמרת כמו שהיא (ייתכן שהסוקט עדכן אותה בינתיים). */
export function mergeChatUsersPage(
  existing: ChatUserModel[] | undefined,
  page: ChatUserModel[],
  append: boolean,
): ChatUserModel[] {
  if (!append || !existing || existing.length === 0) return page;
  const seen = new Set(existing.map((u) => u.UserDisplayPhoneNumber || `id:${u.Id}`));
  const fresh = page.filter((u) => !seen.has(u.UserDisplayPhoneNumber || `id:${u.Id}`));
  return fresh.length ? [...existing, ...fresh] : existing;
}

function chatViewToUsersKey(view: ChatViewId | undefined): ChatViewUsersId {
  if (!view) return 'allChatUsers';
  if (view === 'mySnoozed') return 'mySnoozedChatUsers';
  return `${view}ChatUsers` as ChatViewUsersId;
}

//* מונה בקשות: תשובה שהגיעה אחרי שנשלחה בקשה חדשה יותר (החלפת תצוגה/פילטר תוך כדי
//* טעינת עמוד) נזרקת — אחרת עמוד ישן היה מצטרף לרשימה של פילטר אחר.
let chatUsersRequestSeq = 0;

export const initialFilters: ChatUsersFilters = {
  pageOffset: 1,
  searchQuery: '',
  label: '',
  labels: [],
  open: 'all',
  isOnlyUnread: false,
  assigneeFilter: [],
  dontDisturb: false,
  isBot: false,
  sendFailedOnly: false,
};

const initialState: {
  selectedChatUser: ChatUserModel | null;
  chatUsers: {
    myChatUsers: ChatUserModel[];
    mySnoozedChatUsers: ChatUserModel[];
    notMyChatUsers: ChatUserModel[];
    unassignedChatUsers: ChatUserModel[];
    botsChatUsers: ChatUserModel[];
    allChatUsers: ChatUserModel[];
    customChatUsers: ChatUserModel[];
    completedChatUsers: ChatUserModel[];
    loading: boolean;
  };
  areUsersRequested: {
    myChatUsers: boolean;
    mySnoozedChatUsers: boolean;
    notMyChatUsers: boolean;
    unassignedChatUsers: boolean;
    botsChatUsers: boolean;
    allChatUsers: boolean;
    customChatUsers: boolean;
    completedChatUsers: boolean;
  };
  chatUsersFilters: ChatUsersFilters;
  chatUsersSavedMessages: Array<{
    id: string;
    messages: Array<HistoryMessage | MessageModel>;
  }>;
  /** מועדי תזכורת לפי טלפון מנורמל — שורד רענון רשימה גם כשהשרת לא מחזיר snoozeEndTime */
  snoozeEndByPhone: Record<string, string>;
  lastRequestedCustomViewId?: string;
  /** מצב העימוד של רשימת השיחות לכל תצוגה — ראו getChatUsers (עימוד אמיתי לפי offset) */
  chatUsersPaging: Partial<Record<ChatViewUsersId, ChatUsersPagingState>>;
} = {
  selectedChatUser: null,
  chatUsers: {
    myChatUsers: [],
    mySnoozedChatUsers: [],
    notMyChatUsers: [],
    unassignedChatUsers: [],
    botsChatUsers: [],
    allChatUsers: [],
    customChatUsers: [],
    completedChatUsers: [],
    loading: false,
  },
  areUsersRequested: {
    myChatUsers: false,
    mySnoozedChatUsers: false,
    notMyChatUsers: false,
    unassignedChatUsers: false,
    botsChatUsers: false,
    allChatUsers: false,
    customChatUsers: false,
    completedChatUsers: false,
  },
  chatUsersFilters: initialFilters,
  chatUsersSavedMessages: [],
  snoozeEndByPhone: {},
  lastRequestedCustomViewId: undefined,
  chatUsersPaging: {},
};

const selectedChatUserSlice = createSlice({
  name: 'selectedChatUser',
  initialState: initialState,
  reducers: {
    setChatUsers(
      state,
      action: PayloadAction<{
        myChatUsers?: ChatUserModel[];
        mySnoozedChatUsers?: ChatUserModel[];
        notMyChatUsers?: ChatUserModel[];
        unassignedChatUsers?: ChatUserModel[];
        botsChatUsers?: ChatUserModel[];
        allChatUsers?: ChatUserModel[];
        customChatUsers?: ChatUserModel[];
        completedChatUsers?: ChatUserModel[];
      }>,
    ) {
      if (action.payload.notMyChatUsers) state.chatUsers.notMyChatUsers = action.payload.notMyChatUsers;
      if (action.payload.unassignedChatUsers)
        state.chatUsers.unassignedChatUsers = action.payload.unassignedChatUsers;
      if (action.payload.botsChatUsers) state.chatUsers.botsChatUsers = action.payload.botsChatUsers;
      if (action.payload.allChatUsers) state.chatUsers.allChatUsers = action.payload.allChatUsers;
      if (action.payload.myChatUsers) state.chatUsers.myChatUsers = action.payload.myChatUsers;
      if (action.payload.mySnoozedChatUsers)
        state.chatUsers.mySnoozedChatUsers = action.payload.mySnoozedChatUsers;
      if (action.payload.customChatUsers) state.chatUsers.customChatUsers = action.payload.customChatUsers;
      if (action.payload.completedChatUsers) state.chatUsers.completedChatUsers = action.payload.completedChatUsers;
    },
    setChatUsersLoading(state, action: PayloadAction<boolean>) {
      state.chatUsers.loading = action.payload;
    },
    setChatUsersPaging(state, action: PayloadAction<{ key: ChatViewUsersId } & ChatUsersPagingState>) {
      const { key, ...paging } = action.payload;
      state.chatUsersPaging[key] = paging;
    },
    storeSetChatUsersRequested(
      state,
      action: PayloadAction<Partial<Record<keyof typeof initialState.areUsersRequested, boolean>>>,
    ) {
      const params = action.payload;
      Object.assign(state.areUsersRequested, params);
    },
    storeUpdateSingleChatUser(state, action: PayloadAction<{ id: number; data: Partial<ChatUserModel> }>) {
      const { id, data } = action.payload;
      if (state.selectedChatUser && state.selectedChatUser.Id === id) {
        Object.assign(state.selectedChatUser, data);
      }
      const states = Object.keys(state.chatUsers).filter((objKey) => objKey !== 'loading') as Array<
        keyof Omit<typeof state.chatUsers, 'loading'>
      >;
      states.forEach((key) => {
        const arr = state.chatUsers[key];
        const idx = arr.findIndex((chatUser) => chatUser.Id === id);
        if (idx !== -1) {
          Object.assign(arr[idx], data);
        }
      });
    },
    storeUpdateSingleChatUserByPhone(
      state,
      action: PayloadAction<{ phone: string; data: Partial<ChatUserModel> }>,
    ) {
      const { phone, data } = action.payload;
      if (state.selectedChatUser && state.selectedChatUser.UserDisplayPhoneNumber === phone) {
        Object.assign(state.selectedChatUser, data);
      }
      const states = Object.keys(state.chatUsers).filter((objKey) => objKey !== 'loading') as Array<
        keyof Omit<typeof state.chatUsers, 'loading'>
      >;
      states.forEach((key) => {
        const arr = state.chatUsers[key];
        const idx = arr.findIndex((chatUser) => chatUser.UserDisplayPhoneNumber === phone);
        if (idx !== -1) {
          Object.assign(arr[idx], data);
        }
      });
    },
    setSelectedChatUser(state, action: PayloadAction<ChatUserModel | null>) {
      state.selectedChatUser = action.payload;
    },
    handleChatUsersFilters(state, action: PayloadAction<Partial<ChatUsersFilters>>) {
      const p = action.payload;
      Object.assign(state.chatUsersFilters, p);
      const f = state.chatUsersFilters;
      if ('label' in p && !('labels' in p)) {
        f.labels = f.label ? [f.label] : [];
      } else if ('labels' in p && !('label' in p)) {
        f.label = f.labels.length === 0 ? '' : f.labels[0];
      } else if ('labels' in p && 'label' in p) {
        f.label = f.labels.length === 0 ? '' : f.labels[0];
      }
    },
    setChatUsersSavedMessages(
      state,
      action: PayloadAction<Array<{ id: string; messages: Array<HistoryMessage | MessageModel> }>>,
    ) {
      const { payload } = action;
      state.chatUsersSavedMessages = payload;
    },
    handleUserSavedMessages(
      state,
      action: PayloadAction<{ id: string; messages: Array<HistoryMessage | MessageModel> }>,
    ) {
      const { id, messages } = action.payload;

      const index = state.chatUsersSavedMessages.findIndex((item) => item.id === id);
      if (index !== -1) {
        state.chatUsersSavedMessages[index].messages = messages;
      } else {
        state.chatUsersSavedMessages.push({ id, messages });
      }
    },
    saveSingleMessageToUser(
      state,
      action: PayloadAction<{ id: string; message: HistoryMessage | MessageModel }>,
    ) {
      const { id, message } = action.payload;
      const index = state.chatUsersSavedMessages.findIndex((item) => item.id === id);
      if (index !== -1) {
        const messagesList = state.chatUsersSavedMessages[index].messages;
        const existingMessageIndex = messagesList.findIndex((msg) => {
          const messageIdMatch = msg.Id && message.Id && msg.Id === message.Id;

          const msgTempId = msg.TempId || msg.tempId;
          const messageTempId = message.TempId || message.tempId;
          const tempIdMatch = msgTempId != null && messageTempId != null && msgTempId === messageTempId;
          return messageIdMatch || tempIdMatch;
        });

        if (existingMessageIndex !== -1) {
          // Update the existing message
          state.chatUsersSavedMessages[index].messages[existingMessageIndex] = {
            ...messagesList[existingMessageIndex],
            ...message,
          };
        } else {
          // Add the new message
          state.chatUsersSavedMessages[index].messages.push(message);
        }
      } else {
        // Create a new entry for the user if it doesn't exist
        state.chatUsersSavedMessages.push({ id, messages: [message] });
      }
    },

    resetChat(state) {
      return initialState;
    },
    setLastRequestedCustomViewId(state, action: PayloadAction<string | undefined>) {
      state.lastRequestedCustomViewId = action.payload;
    },
    setSnoozeEndByPhone(
      state,
      action: PayloadAction<{ phone: string; iso: string | null }>,
    ) {
      const key = normalizePhoneForChatUserApi(action.payload.phone);
      if (!key) return;
      if (action.payload.iso) {
        state.snoozeEndByPhone[key] = action.payload.iso;
      } else {
        delete state.snoozeEndByPhone[key];
      }
    },
  },
});

export default selectedChatUserSlice.reducer;
export const {
  setChatUsers,
  setChatUsersLoading,
  setChatUsersPaging,
  storeSetChatUsersRequested,
  storeUpdateSingleChatUser,
  storeUpdateSingleChatUserByPhone,
  setSelectedChatUser,
  setChatUsersSavedMessages,
  handleChatUsersFilters,
  handleUserSavedMessages,
  saveSingleMessageToUser,
  resetChat,
  setLastRequestedCustomViewId,
  setSnoozeEndByPhone,
} = selectedChatUserSlice.actions;

//* handlers
export const setChatUsersRequested = (
  params: Partial<Record<keyof typeof initialState.areUsersRequested, boolean>>,
) => {
  dispatch(storeSetChatUsersRequested({ ...params }));
};
export const updateSingleChatUser = (id: number, data: Partial<ChatUserModel>) => {
  dispatch(storeUpdateSingleChatUser({ id, data }));
};
export const updateSingleChatUserByPhone = (phone: string, data: Partial<ChatUserModel>) => {
  dispatch(storeUpdateSingleChatUserByPhone({ phone, data }));
};

/**
 * הזזת שורה בין רשימות התצוגות לפי מצבה העדכני ב-store: הסרה מכל רשימה שנטענה
 * והכנסה-מחדש רק לרשימות שהיא עדיין תואמת (doesUserMatchCurrentFilters).
 * נקראת אחרי patch במקום (updateSingleChatUserByPhone) משני כיוונים: מטפלי
 * הסוקט (שינוי שהגיע מנציג אחר) והמסלולים האופטימיים של המבצע עצמו — שיוך,
 * סטטוס ידני וסיום שיחה. אצל המבצע ה-patch המקומי רץ לפני ה-echo מהסוקט,
 * וכשההד מגיע ההשוואה "האם השתנה" כבר רואה את הערכים החדשים ומדלגת — ולכן
 * בלי הקריאה כאן השיחה נשארה אצלו בתצוגה הלא-נכונה (נצפה 26.10 בשיוך
 * מ"לא משויכות").
 */
export function relocateChatUserInViewLists(phone: string): void {
  const chatState = store.getState().chat;
  const { chatUsers, areUsersRequested } = chatState;

  let patchedRow: ChatUserModel | undefined;
  for (const key of Object.keys(chatUsers) as Array<keyof typeof chatUsers>) {
    if (key === 'loading') continue;
    patchedRow = (chatUsers[key] as ChatUserModel[]).find(
      (u) => u.UserDisplayPhoneNumber === phone,
    );
    if (patchedRow) break;
  }
  if (!patchedRow) return;

  const rebuilt = (Object.keys(chatUsers) as Array<keyof typeof chatUsers>).reduce(
    (acc, key) => {
      if (key === 'loading' || !areUsersRequested[key as keyof typeof areUsersRequested]) {
        acc[key] = chatUsers[key] as never;
      } else {
        const withoutRow = (chatUsers[key] as ChatUserModel[]).filter(
          (u) => u.UserDisplayPhoneNumber !== phone,
        );
        if (doesUserMatchCurrentFilters(patchedRow!, key)) {
          withoutRow.unshift(patchedRow!);
        }
        acc[key] = withoutRow as never;
      }
      return acc;
    },
    {} as typeof chatUsers,
  );

  dispatch(
    setChatUsers({
      allChatUsers: rebuilt.allChatUsers,
      myChatUsers: rebuilt.myChatUsers,
      mySnoozedChatUsers: rebuilt.mySnoozedChatUsers,
      notMyChatUsers: rebuilt.notMyChatUsers,
      unassignedChatUsers: rebuilt.unassignedChatUsers,
      botsChatUsers: rebuilt.botsChatUsers,
      customChatUsers: rebuilt.customChatUsers,
      completedChatUsers: rebuilt.completedChatUsers,
    }),
  );
}

function dispatchChatUsersForView(view: ChatViewId | undefined, users: ChatUserModel[]) {
  if (!view) return;
  //* מזג מועדי תזכורת (DB + fallback מקומי) ומיין תצוגת תזכורת לפי מועד קרוב-ראשון.
  let enriched = mergeSnoozeTimesIntoChatUsers(stampMissingCompanyIdOnChatUsers(users));
  const workflowFilter = (store.getState().chat.chatUsersFilters.flowStatusCode ?? null) as WorkflowStatusId | null;
  if (shouldSortChatUsersByReminderDate(view, workflowFilter)) {
    enriched = sortChatUsersByReminderDateAsc(enriched);
  }
  const payload: Partial<Record<ChatViewUsersId, ChatUserModel[]>> = {};
  if (view === 'all') payload.allChatUsers = enriched;
  else if (view === 'completed') payload.completedChatUsers = enriched;
  else if (view === 'my') payload.myChatUsers = enriched;
  else if (view === 'mySnoozed') payload.mySnoozedChatUsers = enriched;
  else if (view === 'notMy') payload.notMyChatUsers = enriched;
  else if (view === 'unassigned') payload.unassignedChatUsers = enriched;
  else if (view === 'bots') payload.botsChatUsers = enriched;
  else if (view === 'custom') payload.customChatUsers = enriched;
  dispatch(setChatUsers(payload));
}

//* fetchers
export async function getChatUsers(
  useLoader: boolean = true,
  view?: ChatViewId,
  viewId?: string,
  options?: { append?: boolean },
) {
  //* append=true: העמוד הבא לפי loadedOffset (גלילה אינסופית); אחרת החלפה מהעמוד הראשון.
  //* עד 21.10 כל גלילה משכה את כל הרשימה מחדש עם limit גדל (30/60/90...), והשרת חתך
  //* ב-100 — התנאי "ממוצע 30 לעמוד" נשבר אחרי שלוש גלילות והרשימה נתקעה על 100 שורות.
  const append = Boolean(options?.append);
  const viewKey = chatViewToUsersKey(view);
  const offset = append ? (store.getState().chat.chatUsersPaging[viewKey]?.loadedOffset ?? 0) : 0;
  const requestSeq = ++chatUsersRequestSeq;
  if (useLoader) {
    dispatch(setChatUsersLoading(true));
  }
  try {
    let params: ChatUsersFiltersRequest;
    const chatUsersFilters = store.getState().chat.chatUsersFilters;
    const currentUser = store.getState().user;
    
    // שליפת פילטרים מותאמים עבור תצוגה מותאמת
    let customFilters: import('types/userConfig/userConfig').FilterGroup[] | undefined;
    let customFiltersLogic: 'or' | 'and' | undefined;
    
    if (view === 'custom' && viewId) {
      const customView = store.getState().userConfig.customViews.find(
        (v) => v.id.toString() === viewId,
      );
      if (customView) {
        // המרת תצוגה ישנה למבנה חדש אם צריך
        const migratedView = migrateCustomView(customView);
        if (migratedView.filters.length > 0 && isFilterGroup(migratedView.filters[0])) {
          customFilters = migratedView.filters as import('types/userConfig/userConfig').FilterGroup[];
          customFiltersLogic = migratedView.filtersLogic || 'and';
        }
      }
    }
    
    let flowStatusCodeForView: number | undefined;

    if (chatUsersFilters.sendFailedOnly) {
      customFilters = buildSendFailedFilterGroups();
      customFiltersLogic = 'and';
      flowStatusCodeForView = 3;
    }

    // קביעת flowStatusCode לפי התצוגה או מ-chatUsersFilters
    if (flowStatusCodeForView == null) {
      if (view === 'unassigned') {
        flowStatusCodeForView = 1;
      } else if (view === 'mySnoozed') {
        flowStatusCodeForView = 5;
      } else if (view === 'completed') {
        flowStatusCodeForView = 6;
      } else if (view === 'bots') {
        // תצוגת «בוטים» מציגה כל שיחה שאצל בוט, בלי סינון סטטוס —
        // flowStatusCode שנשאר ב-chatUsersFilters מתצוגת wf קודמת לא דולף לכאן
        flowStatusCodeForView = undefined;
      } else if (chatUsersFilters.flowStatusCode) {
        flowStatusCodeForView = chatUsersFilters.flowStatusCode;
      }
    }

    // בניית assigneeFilter לפי התצוגה
    let assigneeFilterForView: string[] = [];
    
    if (view === 'my' || view === 'mySnoozed') {
      // "שיחות שלי" / הקפאה תחת שלי — אותו API כמו שלי
      if (currentUser?.Udid) {
        assigneeFilterForView = ['my']; // Send 'my' as a flag
      }
    } else if (view === 'notMy') {
      // "שיחות של אחרים" - שלוח את כל ה-UDID של המשתמשים האחרים
      assigneeFilterForView = ['notMy']; // Send 'notMy' as a flag
    } else if (view === 'unassigned') {
      // "לא משויכות" - שלוח 'no-assign'
      assigneeFilterForView = ['no-assign'];
    } else if (view === 'bots') {
      // "בוטים" - לא צריך assigneeFilter, אבל צריך is_bot: true
      assigneeFilterForView = [];
    } else {
      // "כל השיחות" או תצוגות אחרות - לא צריך assigneeFilter
      assigneeFilterForView = chatUsersFilters.assigneeFilter || [];
    }
    
    // עבור תצוגות מותאמות עם פילטרים חדשים, נשתמש בסינון בשרת
    // אם אין פילטרים מותאמים, נביא יותר שיחות כדי לסנן אותן בצד הלקוח
    const hasCustomFilters =
      (view === 'custom' && customFilters && customFilters.length > 0) ||
      Boolean(chatUsersFilters.sendFailedOnly);
    const pageSize = CHAT_LIST_PAGE_SIZE;
    
    // בדוק אם יש פילטר תגית (LableName) בתצוגות מותאמות ושלח אותו ל-API
    let labelForApi = view === 'custom' ? '' : chatUsersFilters.label;
    let assigneeFilterForApi = view === 'custom' ? [] : chatUsersFilters.assigneeFilter;
    
    if (view === 'custom') {
      // עבור תצוגות מותאמות, בדוק גם את הפילטר הרגיל של התגית (label)
      // וגם את הפילטרים של התצוגה המותאמת (filters)
      if (chatUsersFilters.label && chatUsersFilters.label !== '') {
        labelForApi = chatUsersFilters.label;
      } else if (chatUsersFilters.filters) {
      const labelFilter = chatUsersFilters.filters.find((f) => f.key === 'LableName' && f.value != null);
      if (labelFilter) {
        labelForApi = String(labelFilter.value); // הערך הוא כבר JSON.stringify(label)
        }
      }
      
      // בדוק אם יש פילטר משויכת ל (NotificationContactUser) בתצוגות מותאמות
      if (chatUsersFilters.assigneeFilter && chatUsersFilters.assigneeFilter.length > 0) {
        assigneeFilterForApi = chatUsersFilters.assigneeFilter;
      } else if (chatUsersFilters.filters) {
      const assigneeFilter = chatUsersFilters.filters.find((f) => f.key === 'NotificationContactUser' && f.value != null);
      if (assigneeFilter) {
        // הערך יכול להיות string או array
        if (Array.isArray(assigneeFilter.value)) {
          assigneeFilterForApi = assigneeFilter.value;
        } else {
          assigneeFilterForApi = [assigneeFilter.value];
          }
        }
      }
    }

    let labelTokensForApi: string[] = [];
    if (chatUsersFilters.labels && chatUsersFilters.labels.length > 0) {
      labelTokensForApi = [...chatUsersFilters.labels];
    } else if (labelForApi && labelForApi !== '') {
      labelTokensForApi = [labelForApi];
    } else if (chatUsersFilters.label && chatUsersFilters.label !== '') {
      labelTokensForApi = [chatUsersFilters.label];
    }
    const labelParamSingle =
      labelTokensForApi.length === 0 ? '' : labelTokensForApi.length === 1 ? labelTokensForApi[0] : '';
    const labelsParam = labelTokensForApi.length > 1 ? labelTokensForApi : undefined;

    const listOrderBy = view === 'mySnoozed' ? ('snooze_end_date' as const) : ('last_message_date' as const);
    const listOrderDescending = view !== 'mySnoozed';

    if (chatUsersFilters.searchQuery.length) {
      params = {
        page_number: pageSize,
        page_offset: offset,
        filter_by_open: chatUsersFilters.open,
        order_by: listOrderBy,
        order_descending: listOrderDescending,
        search_term: chatUsersFilters.searchQuery,
        label: labelParamSingle,
        labels: labelsParam,
        isOnlyUnread: chatUsersFilters.isOnlyUnread,
        //* תצוגה מותאמת בלי פילטרי שרת: עד 21.10 פילטרי הכותרת (חיפוש/לא-נקראו/פתוח/משויך)
        //* אופסו ונבדקו רק בלקוח על 1000 שורות — עם עמודים של 30 הם חייבים להגיע לשרת.
        assigneeFilter: view === 'custom' && !hasCustomFilters ? assigneeFilterForApi : assigneeFilterForView,
        dontDisturb: chatUsersFilters.dontDisturb,
        filters: [],
        customFilters: hasCustomFilters ? customFilters : undefined,
        customFiltersLogic: hasCustomFilters ? customFiltersLogic : undefined,
        is_bot: view === 'bots' ? true : (view === 'custom' ? false : (chatUsersFilters.isBot ?? false)),
        flowStatusCode: flowStatusCodeForView,
      };
    } else {
      params = {
        page_number: pageSize,
        page_offset: offset,
        filter_by_open: chatUsersFilters.open,
        order_by: listOrderBy,
        order_descending: listOrderDescending,
        search_term: chatUsersFilters.searchQuery,
        label: labelParamSingle,
        labels: labelsParam,
        isOnlyUnread: chatUsersFilters.isOnlyUnread,
        //* תצוגה מותאמת בלי פילטרי שרת: עד 21.10 פילטרי הכותרת (חיפוש/לא-נקראו/פתוח/משויך)
        //* אופסו ונבדקו רק בלקוח על 1000 שורות — עם עמודים של 30 הם חייבים להגיע לשרת.
        assigneeFilter: view === 'custom' && !hasCustomFilters ? assigneeFilterForApi : assigneeFilterForView,
        dontDisturb: chatUsersFilters.dontDisturb,
        filters: [],
        customFilters: hasCustomFilters ? customFilters : undefined,
        customFiltersLogic: hasCustomFilters ? customFiltersLogic : undefined,
        is_bot: view === 'bots' ? true : (view === 'custom' ? false : (chatUsersFilters.isBot ?? false)),
        flowStatusCode: flowStatusCodeForView,
      };
    }

    console.log('params getChatUsers', params);
    const data = await fetchGetChatUsers(params);
    console.log('data getChatUsers', data);
    if (requestSeq !== chatUsersRequestSeq) return; //* בקשה חדשה יותר כבר יצאה
    if (data) {
      if (view === 'custom') {
        if (hasCustomFilters) {
          const pageRows = data?.allChatUsers?.chatUsers ? [...data.allChatUsers.chatUsers] : [];
          dispatch(
            setChatUsers({
              customChatUsers: mergeSnoozeTimesIntoChatUsers(
                mergeChatUsersPage(store.getState().chat.chatUsers.customChatUsers, pageRows, append),
              ),
            }),
          );
        } else {
          const allChats = data?.allChatUsers?.chatUsers || [];
          const chatUsersFilters = store.getState().chat.chatUsersFilters;

          console.log('chatUsersFilters custom', chatUsersFilters);
          console.log('allChats custom', allChats);
          const filteredChats = allChats.filter((chat) =>
            doesUserMatchCurrentFilters(chat, 'customChatUsers'),
          );

          console.log('filteredChats custom', filteredChats);

          dispatch(
            setChatUsers({
              customChatUsers: mergeSnoozeTimesIntoChatUsers(
                mergeChatUsersPage(store.getState().chat.chatUsers.customChatUsers, filteredChats, append),
              ),
            }),
          );
        }
        setChatUsersRequested({
          customChatUsers: true,
        });
      } else {
        const list = data?.allChatUsers?.chatUsers ? [...data.allChatUsers.chatUsers] : [];
        const existing = store.getState().chat.chatUsers[viewKey];
        dispatchChatUsersForView(view, mergeChatUsersPage(existing, list, append));
        setChatUsersRequested({ [viewKey]: true });
      }

      //* מצב העימוד: שורות השרת שנצרכו (לפני סינון לקוחי) + הסכום הכולל — זה מה שקובע
      //* אם יש עוד מה לטעון בגלילה, במקום הניחוש הישן של "ממוצע 30 לעמוד".
      const serverRows = data?.rawContacts?.length ?? data?.allChatUsers?.chatUsers?.length ?? 0;
      dispatch(
        setChatUsersPaging({
          key: viewKey,
          loadedOffset: offset + serverRows,
          total: typeof data?.totalCount === 'number' ? data.totalCount : null,
          lastPageSize: serverRows,
        }),
      );

      const selectedChatUser = store.getState().chat.selectedChatUser;
      if (selectedChatUser) {
        const updatedUser = data?.allChatUsers.chatUsers.find(
          (user: ChatUserModel) => user.Id === selectedChatUser.Id,
        );
        if (updatedUser) {
          dispatch(setSelectedChatUser(updatedUser));
        }
      }

      return {
        allChatUsers: data?.allChatUsers?.chatUsers ? [...data.allChatUsers.chatUsers] : [],
        myChatUsers: data?.myChatUsers?.chatUsers ? [...data.myChatUsers.chatUsers] : [],
      };
    }

    if (!append) dispatchChatUsersForView(view, []);
  } catch (err) {
    console.warn('getChatUsers error: ', err);
    if (!append) dispatchChatUsersForView(view, []);
  } finally {
    //* לא מכבים את הטעינה אם בינתיים יצאה בקשה חדשה — היא זו שתכבה אותה
    if (useLoader && requestSeq === chatUsersRequestSeq) dispatch(setChatUsersLoading(false));
  }
}
export async function setNewUserName(phone: string, newName: string): Promise<boolean> {
  try {
    const formData = {
      phone,
      newName,
    };

    const data = await fetcher(
      'post',
      ['/api/services/UpdateChatUserName', { data: formData }],
      'history',
      // true,
    );

    if (data?.result) {
      updateSingleChatUserByPhone(phone, { FullName: newName });
      return true;
    } else {
      console.log('error editing name');
      return false;
    }
  } catch (error) {
    console.warn('setNewUserName error: ', error);
    return false;
  }
}
export async function handleSetUserLabel(userId: string, option: ChatUserLabel): Promise<boolean> {
  try {
    const labelName = JSON.stringify(option);
    const data = await fetcher(
      'post',
      ['/api/services/SetChatUserLabel', { data: { id: userId, labelName } }],
      'history',
    );
    if (data?.result) {
      updateSingleChatUserByPhone(userId, { LableName: labelName });
      return true;
    }
    return false;
  } catch (error) {
    console.log('SetChatUserLabel error: ', error);
    return false;
  }
}
export async function handleSetUserConversationOpenStatus(userId: string, state: boolean): Promise<boolean> {
  try {
    let sessionId = await resolveSessionIdForMutation(userId);
    if (!sessionId) {
      console.error('Could not find sessionId for user:', userId);
      return false;
    }

    const status = state ? 'open' : 'closed';

    // Routed through server-nodejs so SessionFlowLog records the
    // SESSION_REOPENED / SESSION_CLOSED transition. Plan: 2026-05-17
    const data = await fetcher('put', [`/api/sessions/${sessionId}`, { data: { status } }], 'app');
    if (data === null) return false;
    return true;
  } catch (error) {
    console.error('handleSetUserConversationOpenStatus error:', error);
    return false;
  }
}
export async function handleSetUserAssignee(userId: string, assignee: ApplicationUser | null): Promise<boolean> {
  //* אבחון פרודקשן: מזהה חד-משמעית באיזה שלב שינוי שיוך נופל (resolve / PUT / תשובה)
  let diagStage = 'resolve-session-id';
  let diagSessionId: string | null = null;
  try {
    const sessionId = await resolveSessionIdForMutation(userId);
    if (!sessionId) {
      const selected = store.getState().chat.selectedChatUser;
      console.error('handleSetUserAssignee: could not resolve sessionId', {
        phone: userId,
        selectedPhone: selected?.UserDisplayPhoneNumber ?? null,
        selectedSessionId: selected?.SessionId ?? null,
        selectedFlowStatusCode: selected?.flowStatusCode ?? null,
      });
      return false;
    }
    diagSessionId = sessionId;
    diagStage = 'put-session';

    const managerId = assignee ? assignee.Udid : null;

    // Routed through .NET Server so SessionFlowLog + SignalR session:update
    const data = (await fetcher(
      'put',
      [`/api/sessions/${sessionId}`, { data: { managerId } }],
      'app',
    )) as {
      success?: boolean;
      data?: {
        flowStatusCode?: number;
        flowStatus?: string;
        sessionStatus?: 'open' | 'closed';
        managerId?: string | null;
        manager?: { action?: string };
      };
    } | null;
    if (data === null) {
      console.error('handleSetUserAssignee: PUT /api/sessions returned empty body', {
        sessionId: diagSessionId,
        phone: userId,
      });
      return false;
    }

    // Optimistic UI sync — mirrors what applyFlowStatus does after a flow
    // status PUT. Without this, the chat header / sidebar counters / view
    // tabs / "assigned to" pill only update on the next message:new SignalR
    // event (or a hard refresh). Manager updates may also flip
    // flowStatusCode (first_assign -> 2, unassign -> 1); the route
    // surfaces both the new flow state AND the resolved managerId at the
    // top level of `data` for any non-noop action.
    //
    // The chatUser slice exposes assignee data via the legacy
    // NotificationContactUser (= managerId) / NotificationContactName
    // (= display name) fields, NOT a `managerId` column. Update both so the
    // header, sidebar, and chat-card all re-render in one shot.
    const flat = readSessionPutData(data?.data as UpdateSessionResponse['data']);
    const updates: Partial<ChatUserModel> = {};
    if (typeof flat.flowStatusCode === 'number') {
      updates.flowStatusCode = flat.flowStatusCode as WorkflowStatusId;
    }
    if (typeof flat.flowStatus === 'string') {
      updates.flowStatus = flat.flowStatus;
    }
    if (flat.sessionStatus === 'open' || flat.sessionStatus === 'closed') {
      updates.IsOpen = flat.sessionStatus === 'open';
    }
    // Always update assignee-related fields, even if the route omitted them
    // (noop case where managerId stayed the same — we still want UI to
    // reflect the click). Use the `assignee` we got as a parameter as the
    // source of truth for the displayed name.
    updates.NotificationContactUser = assignee?.Udid ?? null;
    const assigneeDisplayName =
      assignee && `${assignee.FirstName || ''} ${assignee.LastName || ''}`.trim()
        ? `${assignee.FirstName || ''} ${assignee.LastName || ''}`.trim()
        : assignee?.FullName ?? null;
    updates.NotificationContactName = assigneeDisplayName;

    updateSingleChatUserByPhone(userId, updates);
    //* הזזה מיידית בין התצוגות אצל המבצע (למשל היעלמות מ"לא משויכות") —
    //* ה-echo מהסוקט כבר לא יזהה שינוי אחרי ה-patch האופטימי שלמעלה.
    relocateChatUserInViewLists(userId);
    scheduleUnreadCountsRefresh('assignee-optimistic');
    return true;
  } catch (error) {
    console.error('handleSetUserAssignee error:', {
      stage: diagStage,
      sessionId: diagSessionId,
      phone: userId,
      error,
    });
    return false;
  }
}
